import type { Image } from '~publish/legacy/composer/composer/entities/factories'

/**
 * If truncated text ends mid-line, extend to complete the current line.
 * This ensures we don't cut off text in the middle of a sentence when
 * the visual line clamp truncates at a word boundary.
 */
export const extendToLineEnd = (
  truncated: string,
  original: string | undefined,
  opts?: {
    /**
     * Maximum number of characters we're allowed to add beyond `truncated`
     * when extending to the end of the current line.
     *
     * This prevents the "extend" behavior from showing substantially more text
     * than LinkedIn's native app in some layouts.
     */
    maxExtraChars?: number
  },
): string => {
  if (!original || !truncated) return truncated

  // If truncated already ends with newline or is the full text, return as-is
  if (truncated.endsWith('\n') || truncated === original) return truncated

  // Safety check: ensure truncated is actually a prefix of original
  // This prevents unexpected behavior if truncation logic changes
  if (!original.startsWith(truncated)) return truncated

  // Find where the truncated text ends in the original
  const truncatedLength = truncated.length

  // Look for the next newline in the original text after the truncation point
  const nextNewline = original.indexOf('\n', truncatedLength)

  // If there's a newline nearby, extend to include the rest of the current line
  if (nextNewline !== -1) {
    const target = original.slice(0, nextNewline).trimEnd()
    const maxExtraChars = opts?.maxExtraChars
    if (typeof maxExtraChars === 'number' && maxExtraChars >= 0) {
      const maxLength = truncatedLength + maxExtraChars
      if (target.length > maxLength) {
        return original.slice(0, maxLength).trimEnd()
      }
    }
    return target
  }

  // If no newline found, keep the truncated text (it's a single long paragraph)
  return truncated
}

export const formatImagesInRows = (
  images: Image[],
): {
  firstRowCol: Image[]
  secondRowCol: Image[]
} => {
  const total = images.length
  let firstRowCol: Image[] = []
  let secondRowCol: Image[] = []

  if (total >= 5) {
    firstRowCol = images.slice(0, 2)
    secondRowCol = images.slice(2, Math.min(images.length, 5))

    return { firstRowCol, secondRowCol }
  }

  if (total > 0) {
    firstRowCol = images.slice(0, 1)
    secondRowCol = images.slice(1)
  }

  return { firstRowCol, secondRowCol }
}
